import React, { useMemo, useState } from "react";
import moment from "moment";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { RootState } from "../../../../store";
import AttendanceSummary from "./AttendanceSummary";
import useAttendanceSummary from "../../../../hooks/query/useAttendanceSummary";
import { UserMode } from "../../../../interfaces/User";

// eslint-disable-next-line import/prefer-default-export
export function UserAttendance(): React.ReactNode {
  const userCreds = useSelector((state: RootState) => state.userContext.userCreds);
  const resourceCentreId = useSelector((state: RootState) => state.userContext.resourceCentre.id);

  const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf("week"));

  const fromDate = currentWeekStart.format("YYYY-MM-DD");
  const toDate = currentWeekStart.clone().endOf("week").format("YYYY-MM-DD");

  const { data: attendanceSummary = [], isLoading } = useAttendanceSummary({
    resourceCentreId,
    fromDate,
    toDate,
    employeeIds: userCreds.authenticable === UserMode.Employee ? [userCreds.authenticableId] : [],
    serviceProviderIds:
      userCreds.authenticable === UserMode.ServiceProvider ? [userCreds.authenticableId] : []
  });

  const daysOfWeek = useMemo(() => {
    const days = [];
    const day = currentWeekStart.clone();
    for (let i = 0; i < 7; i += 1) {
      days.push(day.clone());
      day.add(1, "day");
    }
    return days;
  }, [currentWeekStart]);

  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().add(1, "week"));
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Attendance Summary</Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handlePrevWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography fontWeight="500">
            {currentWeekStart.format("MMM D")} -{" "}
            {currentWeekStart.clone().endOf("week").format("MMM D, YYYY")}
          </Typography>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AttendanceSummary days={daysOfWeek} attendances={attendanceSummary} />
      )}
    </Box>
  );
}
